import { useMemo } from 'react'
import { type UseQueryResult } from 'react-query'

import useAuth from 'hooks/use-auth'
import { useGremlinQueries } from 'hooks/use-gremlin-queries'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import {
  type RecommendedScenario,
  type Scenario,
  type SharedScenario
} from 'types/api/scenarios/scenario'
import { type UserSessionTeam } from 'types/api/users/sessions/user-session'
import { scenariosGet } from 'utils/endpoints-access'

function createQueriesPerTeam(
  teamsAccess: UserSessionTeam[],
  endpoint: string
) {
  return teamsAccess.map(({ identifier: teamId }) => {
    return {
      endpoint,
      teamId,
      options: {
        placeholderData: [] as Scenario[]
      }
    }
  })
}

function handleQueryResults(queryResult: UseQueryResult<Scenario[]>[]) {
  const reducedQuery = queryResult.reduce(
    (acc, cur) => {
      const { data, isFetching, isError } = cur

      if (data) {
        acc.data = acc.data.concat(data)
      }

      acc.isFetching ||= isFetching
      acc.isError ||= isError

      return acc
    },
    {
      data: [] as Scenario[],
      isFetching: false,
      isError: false
    }
  )

  return reducedQuery
}

/**
 * This hook is designed to fetch all types of scenarios (Published, Draft, Shared, and Recommended) for the teams that
 * the current user has access to. It surfaces the data along with loading and error states for each category of scenarios.
 */
export function useGetScenariosForTeams() {
  const { hasEndpointAccess, teams } = useAuth()

  const publishedAndDraftScenariosQueries = useMemo(() => {
    return createQueriesPerTeam(
      teams.filter((team) =>
        hasEndpointAccess(scenariosGet, {
          teamId: team.identifier
        })
      ),
      '/scenarios'
    )
  }, [hasEndpointAccess, teams])

  /**
   * Fetches scenarios (published and draft combined) for all teams the current user has access to
   */
  const publishedAndDraftScenariosRes = useGremlinQueries(
    publishedAndDraftScenariosQueries
  ) as [UseQueryResult<Scenario[]>]

  const {
    data: publishedAndDraftScenarios,
    isFetching: isFetchingPublishedAndDraftScenarios,
    isError: hasErrorPublishedAndDraftScenarios
  } = useMemo(
    () => handleQueryResults(publishedAndDraftScenariosRes),
    [publishedAndDraftScenariosRes]
  )

  /**
   * Shared and Recommended scenarios are static lists that exist for all teams and do not need to be queried for all
   * teams the user has access to.
   */
  const {
    data: sharedScenarios,
    isFetching: isFetchingSharedScenarios,
    isError: hasErrorSharedScenarios
  } = useGremlinQuery<SharedScenario[]>(
    '/scenarios/shared',
    {},
    {
      placeholderData: [] as SharedScenario[]
    }
  )

  const {
    data: recommendedScenarios,
    isFetching: isFetchingRecommendedScenarios,
    isError: hasErrorRecommendedScenarios
  } = useGremlinQuery<RecommendedScenario[]>(
    '/scenarios/recommended',
    {},
    {
      placeholderData: [] as RecommendedScenario[]
    }
  )

  return {
    // published and draft Scenarios
    publishedAndDraftScenarios,
    isFetchingPublishedAndDraftScenarios,
    hasErrorPublishedAndDraftScenarios,
    // shared Scenarios
    sharedScenarios,
    isFetchingSharedScenarios,
    hasErrorSharedScenarios,
    // recommended Scenarios
    recommendedScenarios,
    isFetchingRecommendedScenarios,
    hasErrorRecommendedScenarios
  }
}
